// src/components/credits/WelcomeOfferModal.js
//
// The welcome offer (mission 008, Fable decisions 008-01 §5b and 008-02 §5):
// "40 % off your first top-up", counting down to the account's real deadline
// (summary.promo.expires_at). Three cards from best_amount_cents — the credits
// value large with its list price struck through and the pay price beneath —
// the best one (the deposit that earns the whole remaining bonus) tagged and
// preselected. "Claim 40 % off — pay $15" reopens Add Funds with the pay
// amount prefilled; "No thanks" closes it. Every percentage shown is the TRUE
// one for its amount (decision 008-02 §7, Astra 03 F4): the headline states
// the preselected card's effective discount. The server grants the bonus when
// the deposit is credited: nothing here changes a price.

import { useState } from 'react';
import { X } from 'lucide-react';
import ModalShell from '../ui/modal-shell';
import { welcomeCards } from '../../lib/addFunds';
import { useCountdown } from '../../hooks/useCountdown';

const PRIMARY = 'flex h-10 w-full items-center justify-center gap-2 rounded-[10px] bg-white px-4 text-[14px] font-semibold text-black hover:bg-zinc-200 active:scale-[0.98] disabled:opacity-50';
const TEXT_BUTTON = 'h-9 px-4 text-[14px] text-zinc-400 hover:text-white';
const usd = (cents) => (cents % 100 ? `$${(cents / 100).toFixed(2)}` : `$${cents / 100}`);

export default function WelcomeOfferModal({ promo, onAccept, onDismiss }) {
  const cards = welcomeCards(promo);
  const best = cards.find((c) => c.best) ?? cards[cards.length - 1] ?? null;
  const [picked, setPicked] = useState(() => best?.pay_cents ?? null);
  const sel = cards.find((c) => c.pay_cents === picked) ?? best;
  const clock = useCountdown(promo?.expires_at);
  const ended = clock.text !== null && clock.done;
  return (
    <ModalShell open onClose={onDismiss} labelledBy="welcome-offer-title" size="sm">
      <div className="flex flex-col gap-5 px-5 pb-5 pt-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex min-w-0 flex-col gap-1.5">
            <span className="text-[11px] font-medium uppercase tracking-wider text-zinc-500">LIMITED OFFER · NEW MEMBERS</span>
            <h2 id="welcome-offer-title" className="text-[22px] font-semibold leading-tight tracking-tight">
              {best?.discount_pct ?? promo?.discount_percent} % off your first top-up
            </h2>
          </div>
          <button type="button" onClick={onDismiss} aria-label="Close"
            className="-mr-2 flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-zinc-500 hover:bg-white/[0.05] hover:text-white">
            <X size={16} />
          </button>
        </div>

        {clock.text !== null && (
          <span role="timer" aria-live="off"
            className={`self-start rounded-full bg-white/5 px-3 py-1 font-mono text-[13px] tabular-nums ${clock.urgent ? 'text-amber-300' : 'text-zinc-300'}`}>
            {ended ? 'Offer ended' : `Ends in ${clock.text}`}
          </span>
        )}

        <div className="grid grid-cols-3 gap-2" role="group" aria-label="Amount">
          {cards.map((c) => {
            const on = sel?.pay_cents === c.pay_cents;
            return (
              <button key={c.pay_cents} type="button" aria-pressed={on} onClick={() => setPicked(c.pay_cents)}
                aria-label={`${usd(c.credits_cents)} of credits for ${usd(c.pay_cents)}${c.best ? ' · Best value' : ''}`}
                className={`relative flex flex-col items-center gap-1 rounded-[12px] border px-2 pb-3 pt-4 text-center transition-colors ${on
                  ? 'border-white/30 bg-[#1E1E1E]' : 'border-white/[0.08] bg-[#181818] hover:bg-[#1D1D1D]'}`}>
                {c.best && (
                  <span className="absolute -top-2 rounded-full bg-white px-2 py-0.5 text-[10px] font-semibold text-black">Best value</span>
                )}
                <span className="font-mono text-[20px] font-semibold tabular-nums text-white">{usd(c.credits_cents)}</span>
                <span className="text-[11px] text-zinc-500">of credits</span>
                <span className="mt-1 flex items-baseline gap-1.5 font-mono text-[13px] tabular-nums">
                  <s className="text-zinc-600">{usd(c.credits_cents)}</s>
                  <span className="font-semibold text-white">{usd(c.pay_cents)}</span>
                </span>
              </button>
            );
          })}
        </div>

        <div className="flex flex-col gap-1">
          <button type="button" disabled={!sel || ended} onClick={() => onAccept?.(sel.pay_cents)} className={PRIMARY}>
            {sel ? `Claim ${sel.discount_pct} % off — pay ${usd(sel.pay_cents)}` : 'Claim'}
          </button>
          <button type="button" onClick={onDismiss} className={`${TEXT_BUTTON} self-center`}>No thanks</button>
        </div>
        <p className="text-center text-[12px] leading-relaxed text-zinc-500">
          Credits are added when your deposit confirms. Up to {usd(promo?.cap_cents ?? 0)} off per account.
        </p>
      </div>
    </ModalShell>
  );
}
